import { useAuthInfo } from "@propelauth/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  Loader2,
  Pause,
  Play,
  Plus,
  Trash2,
  UserPlus,
} from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { useAllAutomations } from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import {
  createAutomation,
  deleteAutomation,
  updateAutomation,
} from "../../../../services/automation/automationEndpoints";
import AlertEditor from "./AlertEditor";
import AssignmentEditor from "./AssignmentEditor";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type AlertDraft = {
  id?: string;
  name: string;
  description: string;
  entity_relation: string;
  filters: any;
  suppression_period: string;
  actions: Array<{
    type: "send_to_slack" | "send_to_email";
    channel_id?: string;
    message?: string;
    to?: string;
    subject?: string;
    body?: string;
  }>;
  status: string;
};

export type AssignmentDraft = {
  id?: string;
  name: string;
  description: string;
  entity_relation: string;
  filters: any;
  assign_mode: string;
  key_role_default_key: string;
  scope_entity: string;
  user_ids: string[];
  max_accounts_per_user?: number | "";
  status: string;
};

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

const AlertsAssignmentPage = () => {
  const { user } = useAuthInfo();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id;
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<"alerts" | "assignment">("alerts");

  /* --- alerts data --- */
  const { data: alertsRaw, isLoading: alertsLoading } = useAllAutomations({
    workspaceId: workspaceId ?? "",
    automationType: "alert",
    enabled: !!workspaceId,
  } as any);
  const alerts: any[] = useMemo(() => (Array.isArray(alertsRaw) ? alertsRaw : []), [alertsRaw]);

  /* --- assignment data --- */
  const { data: assignmentsRaw, isLoading: assignmentsLoading } = useAllAutomations({
    workspaceId: workspaceId ?? "",
    automationType: "assignment",
    enabled: !!workspaceId,
  } as any);
  const assignments: any[] = useMemo(() => (Array.isArray(assignmentsRaw) ? assignmentsRaw : []), [assignmentsRaw]);

  /* --- editor state --- */
  const [editingAlert, setEditingAlert] = useState<AlertDraft | null>(null);
  const [editingAssignment, setEditingAssignment] = useState<AssignmentDraft | null>(null);
  const [saving, setSaving] = useState(false);

  /* ---------------------------------------------------------------- */
  /*  Helpers                                                          */
  /* ---------------------------------------------------------------- */

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["all_automations", workspaceId, "alert"] });
    queryClient.invalidateQueries({ queryKey: ["all_automations", workspaceId, "assignment"] });
  }, [queryClient, workspaceId]);

  /* --- open blank editors --- */
  const newAlert = () =>
    setEditingAlert({
      name: "",
      description: "",
      entity_relation: "accounts",
      filters: { query_type: "AND", predicates: [{ predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] }] },
      suppression_period: "weekly",
      actions: [],
      status: "active",
    });

  const newAssignment = () =>
    setEditingAssignment({
      name: "",
      description: "",
      entity_relation: "accounts",
      filters: { query_type: "AND", predicates: [{ predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] }] },
      assign_mode: "least_accounts",
      key_role_default_key: "csm_id",
      scope_entity: "accounts",
      user_ids: [],
      max_accounts_per_user: "",
      status: "active",
    });

  /* --- open existing for editing --- */
  const editAlert = (a: any) => {
    const actions = a.automation_actions?.actions ?? [];
    setEditingAlert({
      id: a.id,
      name: a.name ?? "",
      description: a.description ?? "",
      entity_relation: a.entity_relation ?? "accounts",
      filters: a.filters ?? { query_type: "AND", predicates: [] },
      suppression_period: a.automation_actions?.suppression_period ?? "weekly",
      actions: actions.filter((act: any) => act.type === "send_to_slack" || act.type === "send_to_email"),
      status: a.status ?? "active",
    });
  };

  const editAssignmentItem = (a: any) => {
    const assignAction = (a.automation_actions?.actions ?? []).find((act: any) => act.type === "assign_person");
    setEditingAssignment({
      id: a.id,
      name: a.name ?? "",
      description: a.description ?? "",
      entity_relation: a.entity_relation ?? "accounts",
      filters: a.filters ?? { query_type: "AND", predicates: [] },
      assign_mode: assignAction?.assign_mode ?? "least_accounts",
      key_role_default_key: assignAction?.key_role_default_key ?? "csm_id",
      scope_entity: assignAction?.scope_entity ?? "accounts",
      user_ids: assignAction?.user_ids ?? [],
      max_accounts_per_user: assignAction?.max_accounts_per_user ?? "",
      status: a.status ?? "active",
    });
  };

  /* --- save alert --- */
  const saveAlert = async (draft: AlertDraft) => {
    if (!workspaceId) return;
    if (!draft.name.trim()) {
      notify({ type: "failure", messageList: ["Name is required"] });
      return;
    }
    if (draft.actions.length === 0) {
      notify({ type: "failure", messageList: ["Add at least one Slack or Email action"] });
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        name: draft.name,
        description: draft.description,
        trigger: "usage_based",
        entity_relation: draft.entity_relation,
        automation_type: "alert",
        is_precreated: false,
        filters: draft.filters,
        metrics: [],
        status: draft.status,
        automation_actions: {
          actions: draft.actions,
          suppression_period: draft.suppression_period,
        },
        time_window: "all_time",
        selected_fields: [],
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        workspace_id: workspaceId,
      };
      if (draft.id) {
        await updateAutomation({ ...payload, automation_id: draft.id, workspace_id: workspaceId });
      } else {
        await createAutomation({ ...payload, owner_id: user?.userId });
      }
      notify({ type: "success", messageList: [draft.id ? "Alert updated" : "Alert created"] });
      setEditingAlert(null);
      invalidate();
    } catch (err: any) {
      notify({ type: "failure", messageList: [err?.response?.data?.detail ?? "Failed to save alert"] });
    } finally {
      setSaving(false);
    }
  };

  /* --- save assignment --- */
  const saveAssignment = async (draft: AssignmentDraft) => {
    if (!workspaceId) return;
    if (!draft.name.trim()) {
      notify({ type: "failure", messageList: ["Name is required"] });
      return;
    }
    setSaving(true);
    try {
      const assignAction: any = {
        type: "assign_person",
        assign_mode: draft.assign_mode,
        key_role_default_key: draft.key_role_default_key,
        scope_entity: draft.scope_entity,
      };
      if (draft.user_ids.length > 0) assignAction.user_ids = draft.user_ids;
      if (draft.max_accounts_per_user !== "" && draft.max_accounts_per_user !== undefined) {
        assignAction.max_accounts_per_user = Number(draft.max_accounts_per_user);
      }
      const payload: any = {
        name: draft.name,
        description: draft.description,
        trigger: "usage_based",
        entity_relation: draft.entity_relation,
        automation_type: "assignment",
        is_precreated: false,
        filters: draft.filters,
        metrics: [],
        status: draft.status,
        automation_actions: {
          actions: [assignAction],
          suppression_period: "all_time",
        },
        time_window: "all_time",
        selected_fields: [],
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        workspace_id: workspaceId,
      };
      if (draft.id) {
        await updateAutomation({ ...payload, automation_id: draft.id, workspace_id: workspaceId });
      } else {
        await createAutomation({ ...payload, owner_id: user?.userId });
      }
      notify({ type: "success", messageList: [draft.id ? "Assignment rule updated" : "Assignment rule created"] });
      setEditingAssignment(null);
      invalidate();
    } catch (err: any) {
      notify({ type: "failure", messageList: [err?.response?.data?.detail ?? "Failed to save assignment rule"] });
    } finally {
      setSaving(false);
    }
  };

  /* --- toggle status --- */
  const toggleStatus = async (item: any, type: "alert" | "assignment") => {
    if (!workspaceId) return;
    const newStatus = item.status === "active" ? "paused" : "active";
    try {
      await updateAutomation({
        automation_id: item.id,
        workspace_id: workspaceId,
        status: newStatus,
      });
      notify({ type: "success", messageList: [`${type === "alert" ? "Alert" : "Assignment"} ${newStatus === "active" ? "activated" : "paused"}`] });
      invalidate();
    } catch (err: any) {
      notify({ type: "failure", messageList: [err?.response?.data?.detail ?? "Failed to update status"] });
    }
  };

  /* --- delete --- */
  const handleDelete = async (item: any, type: "alert" | "assignment") => {
    if (!workspaceId) return;
    try {
      await deleteAutomation({ workspace_id: workspaceId, automation_id: item.id });
      notify({ type: "success", messageList: [`${type === "alert" ? "Alert" : "Assignment rule"} deleted`] });
      invalidate();
    } catch (err: any) {
      notify({ type: "failure", messageList: [err?.response?.data?.detail ?? "Failed to delete"] });
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  const isLoading = alertsLoading || assignmentsLoading;

  // If an editor is open, render it full-page
  if (editingAlert) {
    return (
      <AlertEditor
        draft={editingAlert}
        saving={saving}
        onSave={saveAlert}
        onCancel={() => setEditingAlert(null)}
      />
    );
  }

  if (editingAssignment) {
    return (
      <AssignmentEditor
        draft={editingAssignment}
        saving={saving}
        onSave={saveAssignment}
        onCancel={() => setEditingAssignment(null)}
      />
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Alerts & Assignment</h2>
          <p className="text-[13px] text-muted-foreground">
            Configure automated alerts and account role assignment rules.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="alerts">
              <Bell className="mr-1.5 h-3.5 w-3.5" />
              Alerts
              {alerts.length > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                  {alerts.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="assignment">
              <UserPlus className="mr-1.5 h-3.5 w-3.5" />
              Assignment
              {assignments.length > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                  {assignments.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          <Button
            size="sm"
            onClick={tab === "alerts" ? newAlert : newAssignment}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {tab === "alerts" ? "New Alert" : "New Assignment Rule"}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* ---------- Alerts tab ---------- */}
            <TabsContent value="alerts">
              {alerts.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Bell className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">
                      No alerts configured yet. Create one to get notified via Slack or email when accounts match your conditions.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {alerts.map((a: any) => (
                    <AutomationCard
                      key={a.id}
                      item={a}
                      type="alert"
                      onEdit={() => editAlert(a)}
                      onToggle={() => toggleStatus(a, "alert")}
                      onDelete={() => handleDelete(a, "alert")}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ---------- Assignment tab ---------- */}
            <TabsContent value="assignment">
              {assignments.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <UserPlus className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">
                      No assignment rules configured yet. Create one to automatically assign CSM, AE, or other roles to matching accounts.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {assignments.map((a: any) => (
                    <AutomationCard
                      key={a.id}
                      item={a}
                      type="assignment"
                      onEdit={() => editAssignmentItem(a)}
                      onToggle={() => toggleStatus(a, "assignment")}
                      onDelete={() => handleDelete(a, "assignment")}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Shared card component for list items                               */
/* ------------------------------------------------------------------ */

const SUPPRESSION_LABELS: Record<string, string> = {
  all_time: "Once ever",
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
  first_time_only: "First time only",
  every_occurrence: "Every occurrence",
};

const ROLE_LABELS: Record<string, string> = {
  csm_id: "CSM",
  ae_id: "Account Executive",
  account_champion_id: "Account Champion",
};

const MODE_LABELS: Record<string, string> = {
  least_accounts: "Least Accounts",
  least_revenue: "Least Revenue",
  round_robin: "Round Robin",
  specific: "Specific Users",
};

const AutomationCard = ({
  item,
  type,
  onEdit,
  onToggle,
  onDelete,
}: {
  item: any;
  type: "alert" | "assignment";
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) => {
  const actions = item.automation_actions?.actions ?? [];
  const isActive = item.status === "active";
  const suppressionLabel = SUPPRESSION_LABELS[item.automation_actions?.suppression_period] ?? item.automation_actions?.suppression_period;

  // Build summary chips
  const chips: string[] = [];
  if (type === "alert") {
    const hasSlack = actions.some((a: any) => a.type === "send_to_slack");
    const hasEmail = actions.some((a: any) => a.type === "send_to_email");
    if (hasSlack) chips.push("Slack");
    if (hasEmail) chips.push("Email");
    if (suppressionLabel) chips.push(`Reentry: ${suppressionLabel}`);
  } else {
    const assignAction = actions.find((a: any) => a.type === "assign_person");
    if (assignAction) {
      chips.push(ROLE_LABELS[assignAction.key_role_default_key] ?? assignAction.key_role_default_key);
      chips.push(MODE_LABELS[assignAction.assign_mode] ?? assignAction.assign_mode);
    }
  }

  return (
    <Card className="cursor-pointer transition-colors hover:bg-muted/30" onClick={onEdit}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-medium">{item.name || "Untitled"}</CardTitle>
          <Badge variant={isActive ? "default" : "secondary"} className="text-[10px]">
            {isActive ? "Active" : "Paused"}
          </Badge>
        </div>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onToggle} title={isActive ? "Pause" : "Activate"}>
            {isActive ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onDelete} title="Delete">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pb-3">
        {item.description && (
          <CardDescription className="mb-2 text-xs">{item.description}</CardDescription>
        )}
        <div className="flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <Badge key={c} variant="outline" className="text-[10px] font-normal">
              {c}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default AlertsAssignmentPage;
